import { type BaseAddress } from '~/types/Address';

export const FIELDS_REQUIRE_VALIDATION = [
  'street1',
  'street2',
  'city',
  'zip',
  'country',
  'state',
  'streetNumber',
  'isResidential',
];

export const ADDRESS_VALIDATION_SUPPORTED = new Set([
  'AE',
  'AR',
  'AT',
  'AU',
  'AX',
  'AZ',
  'BA',
  'BD',
  'BE',
  'BG',
  'BH',
  'BM',
  'BN',
  'BR',
  'BS',
  'BW',
  'BY',
  'CA',
  'CH',
  'CL',
  'CO',
  'CN',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EC',
  'EE',
  'EG',
  'ES',
  'FI',
  'FO',
  'FR',
  'GB',
  'GE',
  'GF',
  'GG',
  'GI',
  'GP',
  'GR',
  'GU',
  'HK',
  'HR',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IS',
  'IT',
  'JE',
  'JO',
  'JP',
  'KR',
  'KY',
  'KW',
  'KZ',
  'LB',
  'LI',
  'LT',
  'LU',
  'LV',
  'MA',
  'MC',
  'MD',
  'ME',
  'MK',
  'MO',
  'MP',
  'MQ',
  'MT',
  'MX',
  'MY',
  'NA',
  'NL',
  'NO',
  'NZ',
  'OM',
  'PE',
  'PH',
  'PL',
  'PM',
  'PT',
  'PY',
  'QA',
  'RE',
  'RO',
  'RU',
  'SA',
  'SC',
  'SE',
  'SG',
  'SI',
  'SK',
  'SM',
  'SR',
  'TH',
  'TN',
  'TR',
  'TW',
  'UA',
  'US',
  'UY',
  'VN',
  'WE',
  'YT',
  'ZA',
]);

export enum AddressValidationError {
  ADDRESS_CONFIRMED_INVALID_SECONDARY = 'ADDRESS_CONFIRMED_INVALID_SECONDARY',
  ADDRESS_CONFIRMED_MISSING_SECONDARY = 'ADDRESS_CONFIRMED_MISSING_SECONDARY',
  ADDRESS_INVALID_STREET_DELIVERY = 'ADDRESS_INVALID_STREET_DELIVERY',
  ADDRESS_PREMISE_INVALID = 'ADDRESS_PREMISE_INVALID',
  ADDRESS_PREMISE_MISSING = 'ADDRESS_PREMISE_MISSING',
  ADDRESS_STREET_NOT_FOUND = 'ADDRESS_STREET_NOT_FOUND',
  ADDRESS_SUFFIX_DIRECTION_NOT_FOUND = 'ADDRESS_SUFFIX_DIRECTION_NOT_FOUND',
  ADDRESS_VACANT = 'ADDRESS_VACANT',
  INCOMPLETE_ADDRESS_INPUT = 'INCOMPLETE_ADDRESS_INPUT',
  MULTIPLE_ZIP_PLUS_FOUR_FOUND = 'MULTIPLE_ZIP_PLUS_FOUR_FOUND',
  ZIP_PLUS_FOUR_NOT_FOUND = 'ZIP_PLUS_FOUR_NOT_FOUND',
  ZIP_POST_CODE_NOT_FOUND = 'ZIP_POST_CODE_NOT_FOUND',
}

export const isValidatedAddressV2 = (
  address: { is_confirmed_by_user: boolean; is_valid: boolean } | null | undefined,
  addressValidationIsEnabled: boolean,
) => {
  if (!addressValidationIsEnabled) return true;
  if (!address) return true;
  if (address.is_confirmed_by_user) return true;
  return address.is_valid;
};

export const isValidatedAddress = (
  address: BaseAddress | null | undefined,
  addressValidationIsEnabled = true,
) => {
  if (!addressValidationIsEnabled) return true;
  if (!address) return true;

  const validationResults = address.validationResults;

  // Not all addresses have a validation result.
  // not having a validation result, does not mean they are invalid. they just not have
  // been validated on the backend. so we treat them as valid
  if (!validationResults) return true;

  // customer has confirmed that address is valid
  if (address.isConfirmedByUser) return true;

  return validationResults.isValid;
};

/**
 * Converts a ValidationReasons array to a comma-separated string of reason codes.
 * @param reasons - The array of reason objects from address validation.
 * @returns A comma-separated string of reason codes, or an empty string if none.
 */
export const parseReasonCodesToString = (reasons?: { code: string }[]): string => {
  if (!Array.isArray(reasons) || reasons.length === 0) return '';
  return reasons
    .map((r) => r.code)
    .filter(Boolean)
    .join(',');
};
